import React, { createContext, useContext, useEffect, useState } from 'react';
import api from './api';
import { logDebug } from './devlog';

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Emergent Google Auth race-condition guard (per Emergent auth playbook):
    // when returning from OAuth, the URL fragment carries #session_id=... .
    // Skip the /auth/me probe so the callback route can exchange the session
    // first without a stale 401 racing our state.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // httpOnly cookie is sent automatically by the browser.
    api.get('/auth/me')
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, name, referralCode) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code = referralCode;
    const r = await api.post('/auth/register', payload);
    setUser(r.data.user);
    return r.data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const r = await api.post('/auth/google', { session_id: sessionId });
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // best-effort: even if request fails, drop local user state
      logDebug('[auth] logout request failed, clearing local state anyway:', e?.message);
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, loginWithGoogleSession, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
